/**
 * Slugify Utility
 *
 * Converts a human-readable name into a URL-friendly short name (slug):
 * lowercase letters, numbers, and single hyphens only. Matches the
 * `shortName` validation rule (`^[a-z0-9-]+$`) used by entity forms.
 */

/**
 * Convert an arbitrary string into a URL-friendly slug.
 *
 * Rules:
 * - Lowercased.
 * - Accented characters are folded to their ASCII base where possible.
 * - Any run of characters that is not a lowercase letter or digit becomes a
 *   single hyphen.
 * - Leading and trailing hyphens are trimmed.
 *
 * @param value - The source text (e.g. a Service Name).
 * @returns The slugified value (e.g. "Payments Ledger Service" -> "payments-ledger-service").
 *
 * @example
 * ```ts
 * slugify('Payments Ledger Service'); // 'payments-ledger-service'
 * slugify('  API  Gateway!! '); // 'api-gateway'
 * ```
 */
export function slugify(value: string): string {
  return (
    value
      .normalize('NFKD')
      // Strip combining diacritical marks left after NFKD decomposition.
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      // Replace any run of non-slug characters with a single hyphen.
      .replace(/[^a-z0-9]+/g, '-')
      // Trim leading/trailing hyphens.
      .replace(/^-+|-+$/g, '')
  );
}
